import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : undefined;
  const articles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '资源分类', to: '/category/free-website-builders' },
    { label: category.name },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.to ? `https://www.free-site-nav.com${item.to}` : undefined,
    })),
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 免费资源导航`,
    description: category.description,
    url: `https://www.free-site-nav.com/category/${slug}`,
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 免费资源导航`}
        description={category.description}
        keywords={`${category.name},免费资源,免费网站,建站工具`}
        canonicalUrl={`https://www.free-site-nav.com/category/${slug}`}
        ogTitle={`${category.name} - 免费资源导航`}
        ogDescription={category.description}
        ogUrl={`https://www.free-site-nav.com/category/${slug}`}
        structuredData={{ ...breadcrumbSchema, ...webPageSchema }}
      />

      <Navbar />
      <ScrollToTop />

      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        {/* Category Hero */}
        <section className="w-full bg-background-50 py-8 md:py-12">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">{category.name}</h1>
            <p className="text-sm text-foreground-500 max-w-2xl leading-relaxed">{category.description}</p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full bg-background-100 border-b border-background-200 sticky top-16 z-30">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto overflow-x-auto">
            <div className="flex items-center gap-1 py-3 min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Article List */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-lg md:text-xl font-semibold text-foreground-900 font-heading">
                共 {articles.length} 个资源
              </h2>
            </div>

            {articles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-product-shop>
                {articles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer transition-all duration-300"
                  >
                    <div className="relative w-full h-44 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <span className="text-xs text-foreground-400">{article.date} · {article.readTime}</span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading leading-snug line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center flex-wrap gap-1.5 mt-3">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="px-2 py-0.5 bg-background-200 text-foreground-500 text-xs rounded-full whitespace-nowrap">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-foreground-500">该分类暂无资源，请稍后再来</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}